import { useEffect, useState, type FormEvent } from 'react';
import type { ReviewOrganization } from '@urbanik/api-client';
import { CanonicalCard, TextField } from '@urbanik/ui';
import { reviewApiBase } from './api-base';
const member = '30000000-0000-4000-8000-000000000001';
type Authorization = {
  id: string;
  status: string;
  startsAt: string;
  expiresAt: string;
  maxSubmissions: number;
  usedSubmissions: number;
  reason: string;
  version: number;
  revokeReason: string | null;
  scopes: { field_code: string }[];
};
type Result = {
  affiliation: { status: string; version: number };
  history: { action: string; occurred_at: string }[];
  authorizations: Authorization[];
};
async function call(org: string, path = '', init?: RequestInit) {
  const response = await fetch(
      `${reviewApiBase}/identity/multi-review/organizations/${org}/members/${member}/status-authorizations${path}`,
      {
        ...init,
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'x-urbanik-review': '1',
        },
      },
    ),
    body = await response.json().catch(() => null);
  if (!response.ok) throw new Error(body?.message ?? 'No pudimos completar la solicitud.');
  return body as Result;
}
export function EntityStatusAuthorizationReviewPanel({
  organization,
}: {
  organization: ReviewOrganization;
}) {
  const [data, setData] = useState<Result | null>(null),
    [reason, setReason] = useState('Autorización ficticia para actualización de correo'),
    [error, setError] = useState(''),
    [message, setMessage] = useState(''),
    [busy, setBusy] = useState(false);
  const load = () =>
    void call(organization.id)
      .then(setData)
      .catch((failure) =>
        setError(failure instanceof Error ? failure.message : 'Error inesperado.'),
      );
  useEffect(() => {
    setData(null);
    setError('');
    setMessage('');
    load();
  }, [organization.id]);
  async function create(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      setData(
        await call(organization.id, '', { method: 'POST', body: JSON.stringify({ reason }) }),
      );
      setMessage(`Autorización creada únicamente en ${organization.code}.`);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Error inesperado.');
    } finally {
      setBusy(false);
    }
  }
  async function revoke(item: Authorization) {
    setBusy(true);
    setError('');
    try {
      setData(
        await call(organization.id, `/${item.id}/revoke`, {
          method: 'POST',
          body: JSON.stringify({
            expectedVersion: item.version,
            reason: 'Revocación ficticia solicitada para validar aislamiento',
          }),
        }),
      );
      setMessage(`Autorización revocada únicamente en ${organization.code}.`);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Error inesperado.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <section aria-label={'Estado y autorizaciones de ' + organization.code}>
      <h2>Estado y autorizaciones por entidad</h2>
      <p>
        Estado institucional: <strong>{data?.affiliation.status ?? 'cargando'}</strong> · La
        identidad compartida no cambia.
      </p>
      {error ? (
        <p className="entity-review-banner" role="alert">
          {error}
        </p>
      ) : null}
      {message ? (
        <p className="entity-review-banner" role="status">
          {message}
        </p>
      ) : null}
      <CanonicalCard className="entity-review-card">
        <h3>Ventanas de actualización</h3>
        {data?.authorizations.length ? (
          <ul>
            {data.authorizations.map((item) => (
              <li key={item.id}>
                {item.status} · correo · versión {item.version}
                {item.status === 'active' ? (
                  <button
                    className="ui-button ui-button--secondary"
                    disabled={busy}
                    onClick={() => void revoke(item)}
                  >
                    Revocar con motivo
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p>No existen autorizaciones en esta entidad.</p>
        )}
        <form onSubmit={(event) => void create(event)}>
          <TextField
            id={'authorization-reason-' + organization.id}
            label="Motivo obligatorio (mínimo 10 caracteres)"
            required
            minLength={10}
            maxLength={500}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          <button
            className="ui-button ui-button--primary"
            disabled={busy || reason.trim().length < 10}
          >
            Crear autorización de 7 días en {organization.code}
          </button>
        </form>
      </CanonicalCard>
      <CanonicalCard className="entity-review-card">
        <h3>Historial institucional protegido</h3>
        <p>{data?.history.length ?? 0} eventos de afiliación en esta entidad.</p>
      </CanonicalCard>
    </section>
  );
}
